/**
 * InvoiceListView - Table/List view for invoices with sortable columns and selection
 *
 * Phase 13 Cluster A, Feature B: middle columns (incl. customer/Kunde) are
 * driven by a column-def array so they can be hidden/reordered via per-user
 * localStorage prefs. Only the selection column and the actions column are
 * pinned.
 */
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from '@tanstack/react-router';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import type { Invoice } from '../../hooks/useInvoices';
import {
  moveColumnInVisibleOrder,
  resetColumnPrefs,
  toggleColumn,
  type ColumnPrefs,
} from '../../hooks/useInvoiceColumnPrefs';
import { formatCurrency, formatDate, formatQuarter, getQuarter } from '@armard/shared';
import { FileText, Pencil, ChevronUp, ChevronDown } from 'lucide-react';

export type InvoiceGroupBy = 'none' | 'quarter';

type SortField = 'invoiceNumber' | 'invoiceDate' | 'dueDate' | 'grossTotal' | 'status' | 'createdAt' | 'paymentDate';
type SortOrder = 'asc' | 'desc';

interface ColumnDef {
  key: string;
  label: string;
  sortField?: SortField;
  align?: 'left' | 'right';
  cellClass?: string;
  /** Sprint 006 A3 — Spalten-Priorität: niedrig-prio Spalten fallen bei
   *  schmalen Viewports zuerst (responsive display), Nr./Brutto/Status
   *  fallen NIE. Contained overflow-x-auto bleibt als Fallback. */
  responsiveClass?: string;
  cell: (invoice: Invoice, ctx: { isOverdue: boolean; displayStatus: Invoice['status'] | 'overdue' }) => ReactNode;
}

export const INVOICE_COLUMN_DEFS: readonly ColumnDef[] = [
  {
    key: 'invoiceNumber',
    label: 'Nr.',
    sortField: 'invoiceNumber',
    cell: (invoice) => (
      <span className="text-white font-medium whitespace-nowrap tabular-nums">
        {invoice.invoiceNumber}
      </span>
    ),
  },
  {
    key: 'customer',
    label: 'Kunde',
    responsiveClass: 'hidden sm:table-cell',
    cell: (invoice) => (
      <div className="text-white/70 truncate max-w-xs">
        {invoice.customer?.companyName || 'Kunde'}
      </div>
    ),
  },
  {
    key: 'invoiceDate',
    label: 'Datum',
    sortField: 'invoiceDate',
    responsiveClass: 'hidden md:table-cell',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">{formatDate(invoice.invoiceDate)}</div>
    ),
  },
  {
    key: 'dueDate',
    label: 'Fällig',
    sortField: 'dueDate',
    responsiveClass: 'hidden lg:table-cell',
    cell: (invoice, { isOverdue }) => (
      <div className={`text-sm ${isOverdue ? 'text-red-400' : 'text-white/60'}`}>
        {formatDate(invoice.dueDate)}
      </div>
    ),
  },
  {
    key: 'netTotal',
    label: 'Netto',
    align: 'right',
    cellClass: 'text-right',
    responsiveClass: 'hidden lg:table-cell',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">
        {formatCurrency(parseFloat(invoice.netTotal))}
      </div>
    ),
  },
  {
    key: 'vatAmount',
    label: 'USt',
    align: 'right',
    cellClass: 'text-right',
    responsiveClass: 'hidden xl:table-cell',
    cell: (invoice) => (
      <div className="text-white/50 text-sm">
        {formatCurrency(parseFloat(invoice.vatAmount))}
      </div>
    ),
  },
  {
    key: 'grossTotal',
    label: 'Brutto',
    sortField: 'grossTotal',
    align: 'right',
    cellClass: 'text-right',
    cell: (invoice) => (
      <div className="text-white font-semibold">
        {formatCurrency(parseFloat(invoice.grossTotal))}
      </div>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    sortField: 'status',
    cell: (_invoice, { displayStatus }) => <InvoiceStatusBadge status={displayStatus} />,
  },
  {
    key: 'paymentDate',
    label: 'Bezahlt',
    sortField: 'paymentDate',
    responsiveClass: 'hidden xl:table-cell',
    cell: (invoice) => (
      <div className="text-white/60 text-sm">
        {invoice.paymentDate ? formatDate(invoice.paymentDate) : '—'}
      </div>
    ),
  },
];

export const DEFAULT_INVOICE_COLUMN_ORDER: readonly string[] = INVOICE_COLUMN_DEFS.map(
  (c) => c.key
);

interface InvoiceListViewProps {
  invoices: Invoice[];
  sortBy?: SortField;
  sortOrder?: SortOrder;
  onSortChange?: (sortBy: SortField, sortOrder: SortOrder) => void;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onSelectAll?: () => void;
  columnPrefs?: ColumnPrefs;
  onColumnPrefsChange?: (next: ColumnPrefs) => void;
  groupBy?: InvoiceGroupBy;
  dateBasis?: 'invoiceDate' | 'paymentDate';
}

interface ColumnHeaderProps {
  column: ColumnDef;
  currentSort?: SortField;
  currentOrder?: SortOrder;
  onSort?: (field: SortField, order: SortOrder) => void;
  prefs?: ColumnPrefs;
  onPrefsChange?: (next: ColumnPrefs) => void;
  visibleOrder: readonly string[];
  canHide: boolean;
}

/**
 * Column-scoped header that combines sort-on-click-label with an inline
 * per-column ⋮ menu (sortieren / ausblenden / links / rechts). Puts column
 * management at the column itself rather than in a detached top-row
 * dropdown, matching Notion/Airtable/Linear idioms.
 */
function ColumnHeader({
  column,
  currentSort,
  currentOrder,
  onSort,
  prefs,
  onPrefsChange,
  visibleOrder,
  canHide,
}: ColumnHeaderProps) {
  const isActive = column.sortField !== undefined && currentSort === column.sortField;
  const isSortable = column.sortField !== undefined && !!onSort;
  const canManage = !!prefs && !!onPrefsChange;
  const align = column.align ?? 'left';

  const handleLabelClick = () => {
    if (!isSortable || !column.sortField || !onSort) return;
    if (isActive) {
      onSort(column.sortField, currentOrder === 'asc' ? 'desc' : 'asc');
    } else {
      const defaultDesc =
        column.sortField === 'grossTotal' ||
        column.sortField === 'invoiceDate' ||
        column.sortField === 'dueDate' ||
        column.sortField === 'paymentDate';
      onSort(column.sortField, defaultDesc ? 'desc' : 'asc');
    }
  };

  const visibleIdx = visibleOrder.indexOf(column.key);
  const canMoveLeft = canManage && visibleIdx > 0;
  const canMoveRight = canManage && visibleIdx >= 0 && visibleIdx < visibleOrder.length - 1;

  // dnd-kit: make this header a sortable item
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key, disabled: !canManage });

  const dragStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : undefined,
    cursor: canManage ? (isDragging ? 'grabbing' : 'grab') : undefined,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <th
      ref={setNodeRef}
      style={dragStyle}
      {...attributes}
      {...listeners}
      className={`${align === 'right' ? 'text-right' : 'text-left'} group relative px-4 py-3 text-xs font-medium uppercase tracking-wider select-none ${
        isActive ? 'text-white/80' : 'text-white/40'
      } ${isDragging ? 'bg-white/5' : ''} ${column.responsiveClass ?? ''}`}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        <span
          className={isSortable ? 'cursor-pointer hover:text-white/70 transition-colors' : ''}
          onClick={handleLabelClick}
          onPointerDown={(e) => {
            // prevent dnd-kit from kidnapping the click-to-sort pointerdown
            if (isSortable) e.stopPropagation();
          }}
        >
          <span className="inline-flex items-center gap-1">
            {column.label}
            {isActive && (currentOrder === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
          </span>
        </span>
        {canManage && (
          <ColumnHeaderMenu
            column={column}
            isSortable={isSortable}
            currentSort={currentSort}
            currentOrder={currentOrder}
            canMoveLeft={canMoveLeft}
            canMoveRight={canMoveRight}
            canHide={canHide}
            onSort={(order) => {
              if (!column.sortField || !onSort) return;
              onSort(column.sortField, order);
            }}
            onHide={() => onPrefsChange!(toggleColumn(prefs!, column.key))}
            onMoveLeft={() => onPrefsChange!(moveColumnInVisibleOrder(prefs!, visibleOrder, column.key, 'left'))}
            onMoveRight={() => onPrefsChange!(moveColumnInVisibleOrder(prefs!, visibleOrder, column.key, 'right'))}
          />
        )}
      </span>
    </th>
  );
}

interface ColumnHeaderMenuProps {
  column: ColumnDef;
  isSortable: boolean;
  currentSort?: SortField;
  currentOrder?: SortOrder;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canHide: boolean;
  onSort: (order: SortOrder) => void;
  onHide: () => void;
  onMoveLeft: () => void;
  onMoveRight: () => void;
}

function ColumnHeaderMenu({
  column,
  isSortable,
  currentSort,
  currentOrder,
  canMoveLeft,
  canMoveRight,
  canHide,
  onSort,
  onHide,
  onMoveLeft,
  onMoveRight,
}: ColumnHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const isSortActive = isSortable && currentSort === column.sortField;

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const hasAnyAction = isSortable || canHide || canMoveLeft || canMoveRight;
  if (!hasAnyAction) return null;

  return (
    <span className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Aktionen für Spalte ${column.label}`}
        className="
          inline-flex items-center justify-center w-5 h-5 rounded
          text-white/40 hover:text-white hover:bg-white/10
          focus:text-white focus:bg-white/10 focus:outline-none
          group-hover:text-white/70
          transition-colors
        "
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 5v.01M12 12v.01M12 19v.01" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1 z-50 w-56 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl py-1 normal-case tracking-normal font-normal origin-top-right"
          >
            {isSortable && (
              <>
                <MenuItem
                  active={isSortActive && currentOrder === 'asc'}
                  onClick={() => { onSort('asc'); setOpen(false); }}
                  icon={<ChevronUp className="w-3.5 h-3.5" />}
                  label="Aufsteigend sortieren"
                />
                <MenuItem
                  active={isSortActive && currentOrder === 'desc'}
                  onClick={() => { onSort('desc'); setOpen(false); }}
                  icon={<ChevronDown className="w-3.5 h-3.5" />}
                  label="Absteigend sortieren"
                />
                <MenuDivider />
              </>
            )}
            <MenuItem
              disabled={!canMoveLeft}
              onClick={() => { onMoveLeft(); setOpen(false); }}
              icon={
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              }
              label="Nach links verschieben"
            />
            <MenuItem
              disabled={!canMoveRight}
              onClick={() => { onMoveRight(); setOpen(false); }}
              icon={
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              }
              label="Nach rechts verschieben"
            />
            {canHide && (
              <>
                <MenuDivider />
                <MenuItem
                  onClick={() => { onHide(); setOpen(false); }}
                  icon={
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  }
                  label="Spalte ausblenden"
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </span>
  );
}

function MenuItem({
  onClick,
  icon,
  label,
  active,
  disabled,
}: {
  onClick: () => void;
  icon: ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className={`
        w-full px-3 py-1.5 flex items-center gap-2 text-left text-sm transition-colors
        ${disabled ? 'text-white/25 cursor-not-allowed' : 'text-white/80 hover:bg-white/10'}
        ${active ? 'text-white bg-white/5' : ''}
      `}
    >
      <span className={`shrink-0 ${active ? 'text-white' : 'text-white/40'}`}>{icon}</span>
      <span>{label}</span>
    </button>
  );
}

function MenuDivider() {
  return <div className="my-1 border-t border-white/10" />;
}

/**
 * Column-manager cell at the end of the header row. Always rendered when
 * prefs management is wired up — not just when columns are hidden. Click
 * the trigger to see every available column with a checkmark for the
 * visible ones and an empty circle for the hidden ones; click to toggle.
 * Reset action is surfaced at the bottom only when prefs differ from the
 * factory default. This gives a single place to answer "what columns
 * exist and which are on" without having to hunt.
 */
function ColumnManagerCell({
  prefs,
  onPrefsChange,
  hasNonDefaultPrefs,
}: {
  prefs: ColumnPrefs;
  onPrefsChange: (next: ColumnPrefs) => void;
  hasNonDefaultPrefs: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  // Render columns in current preference order so the menu mirrors the
  // header layout. Columns that don't exist in prefs fall back to the
  // def-array order (shouldn't happen after reconcile, defensive).
  const orderedDefs = useMemo<ColumnDef[]>(() => {
    const byKey = new Map(INVOICE_COLUMN_DEFS.map((c) => [c.key, c]));
    const list: ColumnDef[] = [];
    const seen = new Set<string>();
    for (const key of prefs.order) {
      const def = byKey.get(key);
      if (def) {
        list.push(def);
        seen.add(key);
      }
    }
    for (const def of INVOICE_COLUMN_DEFS) {
      if (!seen.has(def.key)) list.push(def);
    }
    return list;
  }, [prefs.order]);

  const hiddenSet = new Set(prefs.hidden);
  const visibleCount = orderedDefs.length - hiddenSet.size;

  return (
    <th ref={rootRef} className="relative w-10 px-2 py-3 text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Spalten verwalten (${visibleCount} von ${orderedDefs.length} sichtbar)`}
        className="
          inline-flex items-center justify-center w-5 h-5 rounded
          text-white/40 hover:text-white hover:bg-white/10
          focus:text-white focus:bg-white/10 focus:outline-none
          transition-colors
        "
        title={`Spalten (${visibleCount}/${orderedDefs.length})`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h10" />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, scale: 0.96, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1 z-50 w-60 bg-black/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-xl py-1 normal-case tracking-normal font-normal origin-top-right"
          >
            <div className="px-3 pt-1.5 pb-1 text-[10px] font-medium text-white/40 uppercase tracking-wider">
              Spalten ({visibleCount}/{orderedDefs.length})
            </div>
            <ul className="max-h-72 overflow-y-auto">
              {orderedDefs.map((col) => {
                const visible = !hiddenSet.has(col.key);
                const isLastVisible = visible && visibleCount === 1;
                return (
                  <li key={col.key}>
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={visible}
                      disabled={isLastVisible}
                      onClick={() => {
                        if (isLastVisible) return;
                        onPrefsChange(toggleColumn(prefs, col.key));
                      }}
                      title={isLastVisible ? 'Mindestens eine Spalte muss sichtbar bleiben' : undefined}
                      className={`
                        w-full px-3 py-1.5 flex items-center gap-2 text-left text-sm transition-colors
                        ${isLastVisible ? 'cursor-not-allowed' : 'hover:bg-white/10 cursor-pointer'}
                        ${visible ? 'text-white/90' : 'text-white/50'}
                      `}
                    >
                      <span className={`shrink-0 w-3.5 h-3.5 inline-flex items-center justify-center`}>
                        {visible ? (
                          <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.25} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <span className="w-2.5 h-2.5 rounded-full border border-white/25" />
                        )}
                      </span>
                      <span>{col.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {hasNonDefaultPrefs && (
              <>
                <MenuDivider />
                <MenuItem
                  onClick={() => {
                    onPrefsChange(
                      resetColumnPrefs(INVOICE_COLUMN_DEFS.map((c) => c.key))
                    );
                    setOpen(false);
                  }}
                  icon={
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  }
                  label="Auf Standard zurücksetzen"
                />
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </th>
  );
}

interface QuarterGroup {
  key: string;
  quarter: 1 | 2 | 3 | 4;
  year: number;
  invoices: Invoice[];
  totalNet: number;
  totalGross: number;
  totalOpen: number;
}

// Invoices whose gross amount counts as outstanding receivables. Matches
// the dashboard's `OutstandingInvoicesWidget` semantics: only issued,
// collectible invoices. Drafts are not yet sent, deferred invoices are
// intentionally paused, and paid/cancelled are closed out.
const OPEN_STATUSES: ReadonlySet<Invoice['status']> = new Set([
  'sent',
  'overdue',
]);

export function InvoiceListView({
  invoices,
  sortBy,
  sortOrder,
  onSortChange,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  columnPrefs,
  onColumnPrefsChange,
  groupBy = 'none',
  dateBasis = 'invoiceDate',
}: InvoiceListViewProps) {
  const navigate = useNavigate();
  const hasSelection = selectedIds !== undefined;
  const allSelected = hasSelection && invoices.length > 0 && selectedIds.size === invoices.length;

  // Resolve which middle columns to render, in order.
  const defByKey = new Map(INVOICE_COLUMN_DEFS.map((d) => [d.key, d]));
  const order = columnPrefs?.order ?? DEFAULT_INVOICE_COLUMN_ORDER;
  const hidden = new Set(columnPrefs?.hidden ?? []);
  const middleColumns = order
    .map((key) => defByKey.get(key))
    .filter((d): d is ColumnDef => !!d && !hidden.has(d.key));

  const quarterGroups = useMemo<QuarterGroup[] | null>(() => {
    if (groupBy !== 'quarter') return null;
    const map = new Map<string, QuarterGroup>();
    for (const invoice of invoices) {
      const date = invoice[dateBasis];
      const { quarter, year } = date ? getQuarter(date) : { quarter: 1 as const, year: 0 };
      const key = date ? `${year}-${quarter}` : 'unknown';
      let group = map.get(key);
      if (!group) {
        group = {
          key,
          quarter,
          year,
          invoices: [],
          totalNet: 0,
          totalGross: 0,
          totalOpen: 0,
        };
        map.set(key, group);
      }
      const gross = parseFloat(invoice.grossTotal);
      group.invoices.push(invoice);
      group.totalNet += parseFloat(invoice.netTotal);
      group.totalGross += gross;
      if (OPEN_STATUSES.has(invoice.status)) {
        group.totalOpen += gross;
      }
    }
    // Order groups chronologically (newest first) regardless of how the
    // underlying invoice list is sorted. Invoices within each group keep
    // the sort order from the server response.
    return Array.from(map.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });
  }, [invoices, groupBy, dateBasis]);

  // The column-manager cell is always rendered when prefs are available,
  // so users can see "what columns exist / which are on" from one spot.
  // Reset is only surfaced when prefs differ from default.
  const hasNonDefaultPrefs = useMemo(() => {
    if (!columnPrefs) return false;
    if (columnPrefs.hidden.length > 0) return true;
    if (columnPrefs.order.length !== DEFAULT_INVOICE_COLUMN_ORDER.length) return true;
    return columnPrefs.order.some((k, i) => k !== DEFAULT_INVOICE_COLUMN_ORDER[i]);
  }, [columnPrefs]);

  const showColumnManager = !!columnPrefs && !!onColumnPrefsChange;

  // +1 action column, +1 if column-manager shown
  const totalColumns =
    (hasSelection ? 1 : 0) + middleColumns.length + (showColumnManager ? 1 : 0) + 1;

  // dnd-kit setup — sensors tuned so plain clicks (sort, menu button) don't
  // trigger drags. 8px activation distance is the community sweet spot.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!columnPrefs || !onColumnPrefsChange) return;
    const oldIndex = columnPrefs.order.indexOf(String(active.id));
    const newIndex = columnPrefs.order.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const next = [...columnPrefs.order];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    onColumnPrefsChange({ ...columnPrefs, order: next });
  };

  const handleRowClick = (invoiceId: string) => {
    navigate({ to: '/invoices/$id', params: { id: invoiceId } });
  };

  const handleEdit = (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    navigate({ to: '/invoices/$id/edit', params: { id: invoiceId } });
  };

  const handlePdfDownload = (e: React.MouseEvent, invoiceId: string) => {
    e.stopPropagation();
    window.open(`/api/v1/invoices/${invoiceId}/pdf`, '_blank');
  };

  const handleCheckboxClick = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onToggleSelect?.(id);
  };

  const renderInvoiceRow = (invoice: Invoice) => {
    const isOverdue = invoice.status === 'sent' && new Date(invoice.dueDate) < new Date();
    const displayStatus = isOverdue ? 'overdue' : invoice.status;
    const isSelected = selectedIds?.has(invoice.id) ?? false;

    return (
      <tr
        key={invoice.id}
        onClick={() => handleRowClick(invoice.id)}
        className={`hover:bg-white/2 transition-colors cursor-pointer ${
          isSelected ? 'bg-white/5' : ''
        }`}
      >
        {hasSelection && (
          <td className="w-10 px-3 py-4">
            <input
              type="checkbox"
              checked={isSelected}
              onClick={(e) => handleCheckboxClick(e, invoice.id)}
              onChange={() => {}}
              className="w-5 h-5 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer"
            />
          </td>
        )}
        {middleColumns.map((c) => (
          <td key={c.key} className={`px-4 py-4 ${c.cellClass ?? ''} ${c.responsiveClass ?? ''}`}>
            {c.cell(invoice, { isOverdue, displayStatus })}
          </td>
        ))}
        <td className="px-4 py-4 text-right">
          <div className="flex items-center justify-end gap-1">
            {invoice.status === 'draft' && (
              <button
                onClick={(e) => handleEdit(e, invoice.id)}
                className="p-2 rounded-lg hover:bg-white/8 transition-colors"
                title="Bearbeiten"
              >
                <Pencil className="w-4 h-4 text-white/40 hover:text-white/70" />
              </button>
            )}
            <button
              onClick={(e) => handlePdfDownload(e, invoice.id)}
              className="p-2 rounded-lg hover:bg-white/8 transition-colors"
              title="PDF herunterladen"
            >
              <FileText className="w-4 h-4 text-white/40 hover:text-white/70" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="bg-white/[0.02] border border-white/[0.05] rounded-xl backdrop-blur-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <tr className="border-b border-white/6">
                {hasSelection && (
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onSelectAll}
                      className="w-5 h-5 rounded border-white/20 bg-white/5 text-white accent-white/80 cursor-pointer"
                    />
                  </th>
                )}
                <SortableContext items={middleColumns.map((c) => c.key)} strategy={horizontalListSortingStrategy}>
                  {middleColumns.map((c) => (
                    <ColumnHeader
                      key={c.key}
                      column={c}
                      currentSort={sortBy}
                      currentOrder={sortOrder}
                      onSort={onSortChange}
                      prefs={columnPrefs}
                      onPrefsChange={onColumnPrefsChange}
                      visibleOrder={middleColumns.map((col) => col.key)}
                      canHide={middleColumns.length > 1}
                    />
                  ))}
                </SortableContext>
                {showColumnManager && (
                  <ColumnManagerCell
                    prefs={columnPrefs!}
                    onPrefsChange={onColumnPrefsChange!}
                    hasNonDefaultPrefs={hasNonDefaultPrefs}
                  />
                )}
                <th className="text-right px-4 py-3 text-xs font-medium text-white/40 uppercase tracking-wider w-24">
                </th>
              </tr>
            </DndContext>
          </thead>
          <tbody className="divide-y divide-white/6">
            {quarterGroups
              ? quarterGroups.map((group) => (
                  <Fragment key={group.key}>
                    {/* A4: Summary-Row — sticky entfernt (wirkte im
                        overflow-x-Container nie vertikal); tabular-nums
                        für ruhige Zahlenkolonnen. */}
                    <tr className="bg-white/[0.04] border-t border-white/10">
                      <td
                        colSpan={totalColumns}
                        className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider"
                      >
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <span className="text-white/80">
                            {group.key === 'unknown' ? 'Zahlungstag unbekannt' : formatQuarter(group.quarter, group.year)}
                            <span className="text-white/40 ml-2 normal-case">
                              · {group.invoices.length} Rechnung
                              {group.invoices.length !== 1 ? 'en' : ''}
                            </span>
                          </span>
                          <span className="text-white/60 normal-case tabular-nums">
                            Seitensumme · Netto {formatCurrency(group.totalNet)}
                            <span className="mx-2 text-white/30">·</span>
                            <span className="text-white/80 font-medium">
                              Brutto {formatCurrency(group.totalGross)}
                            </span>
                            {group.totalOpen > 0 && (
                              <>
                                <span className="mx-2 text-white/30">·</span>
                                <span className="text-amber-300/80">
                                  Offen {formatCurrency(group.totalOpen)}
                                </span>
                              </>
                            )}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.invoices.map((invoice) =>
                      renderInvoiceRow(invoice)
                    )}
                  </Fragment>
                ))
              : invoices.map((invoice) => renderInvoiceRow(invoice))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InvoiceListView;
